import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Edit3, Trash2, Save, X } from 'lucide-react';
import timetableService from '../../services/timetableService';
import '../../styles/ClassSettings.css';

const ClassSettings = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState(null);
  const [mode, setMode] = useState('Modify'); // Add, Modify, Delete, Cancel, Save
  const [formData, setFormData] = useState({
    class_code: '',
    class_key: '',
    class_name: '',
    class_level: 1,
    class_section: '',
    home_room: ''
  });

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      const data = await timetableService.getClasses();
      setClasses(data);
      if (data.length > 0 && !selectedClass) {
        setSelectedClass(data[0]);
        setFormData({
          class_code: data[0].class_code || '',
          class_key: data[0].class_key || '',
          class_name: data[0].class_name || '',
          class_level: data[0].class_level || 1,
          class_section: data[0].class_section || '',
          home_room: data[0].home_room || ''
        });
      }
    } catch (error) {
      console.error('Error loading classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClassSelect = (classItem) => {
    setSelectedClass(classItem);
    setFormData({
      class_code: classItem.class_code || '',
      class_key: classItem.class_key || '',
      class_name: classItem.class_name || '',
      class_level: classItem.class_level || 1,
      class_section: classItem.class_section || '',
      home_room: classItem.home_room || ''
    });
    setMode('Modify');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAdd = () => {
    setMode('Add');
    setFormData({
      class_code: '',
      class_key: '',
      class_name: '',
      class_level: 1,
      class_section: '',
      home_room: ''
    });
  };

  const handleModify = () => {
    setMode('Modify');
  };

  const handleDelete = async () => {
    if (selectedClass && window.confirm('確定要刪除此班別嗎？')) {
      try {
        await timetableService.deleteClass(selectedClass.id);
        await loadClasses();
        setMode('Modify');
      } catch (error) {
        console.error('Error deleting class:', error);
      }
    }
  };

  const handleCancel = () => {
    if (selectedClass) {
      setFormData({
        class_code: selectedClass.class_code || '',
        class_key: selectedClass.class_key || '',
        class_name: selectedClass.class_name || '',
        class_level: selectedClass.class_level || 1,
        class_section: selectedClass.class_section || '',
        home_room: selectedClass.home_room || ''
      });
    }
    setMode('Modify');
  };

  const handleSave = async () => {
    try {
      if (mode === 'Add') {
        await timetableService.createClass(formData);
      } else if (mode === 'Modify' && selectedClass) {
        await timetableService.updateClass(selectedClass.id, formData);
      }
      await loadClasses();
      setMode('Modify');
    } catch (error) {
      console.error('Error saving class:', error);
    }
  };
  if (loading) {
    return (
      <div className="class-settings-container">
        <div className="loading-container">
          <div className="loading-content">
            <div className="loading-spinner">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
            </div>
            <p className="loading-text">載入班別資料中...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="class-settings-container">
      {/* Header */}
      <div className="class-settings-header">
        <h2>Classes [TBM0140E]</h2>
        <p className="subtitle">班別設定 - 管理班別資料和編修</p>
      </div>

      {/* Main Content */}
      <div className="class-settings-content">
        {/* Class Form Panel */}
        <div className="class-form-panel">
          <h3 className="class-form-title">Classes [TBM0140E]</h3>
          
          <div className="class-form-grid">
            {/* Class Code */}
            <div className="class-form-group">
              <label className="class-form-label">Class Code:</label>
              <input
                type="text"
                value={formData.class_code}
                onChange={(e) => handleInputChange('class_code', e.target.value)}
                className="class-form-input"
                placeholder="1A"
              />
            </div>

            {/* Class Key */}
            <div className="class-form-group">
              <label className="class-form-label">Class Key:</label>
              <input
                type="text"
                value={formData.class_key}
                onChange={(e) => handleInputChange('class_key', e.target.value)}
                className="class-form-input"
                placeholder="1A"
              />
            </div>

            {/* Class Name */}
            <div className="class-form-group">
              <label className="class-form-label">Class Name:</label>
              <input
                type="text"
                value={formData.class_name}
                onChange={(e) => handleInputChange('class_name', e.target.value)}
                className="class-form-input"
                placeholder="1A"
              />
            </div>

            {/* Class Level */}
            <div className="class-form-group">
              <label className="class-form-label">Class Level:</label>
              <input
                type="number"
                value={formData.class_level}
                onChange={(e) => handleInputChange('class_level', parseInt(e.target.value) || 1)}
                className="class-number-input"
                min="1"
                max="12"
              />
            </div>

            {/* Class Section */}
            <div className="class-form-group">
              <label className="class-form-label">Class Section:</label>
              <input
                type="text"
                value={formData.class_section}
                onChange={(e) => handleInputChange('class_section', e.target.value)}
                className="class-form-input"
                placeholder="A"
              />
            </div>

            {/* Home Room */}
            <div className="class-form-group">
              <label className="class-form-label">Home Room:</label>
              <input
                type="text"
                value={formData.home_room}
                onChange={(e) => handleInputChange('home_room', e.target.value)}
                className="class-form-input"
                placeholder="101"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons Panel */}
        <div className="action-buttons-panel">
          <button
            onClick={() => window.history.back()}
            className="action-button secondary"
          >
            <X size={16} />
            Close
          </button>
          <button
            onClick={handleAdd}
            className="action-button success"
          >
            <Plus size={16} />
            Add
          </button>
          <button
            onClick={handleModify}
            className="action-button primary"
          >
            <Edit3 size={16} />
            Modify
          </button>
          <button
            onClick={handleDelete}
            className="action-button danger"
          >
            <Trash2 size={16} />
            Delete
          </button>
          <button
            onClick={handleCancel}
            className="action-button secondary"
          >
            <X size={16} />
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="action-button success"
          >
            <Save size={16} />
            Save
          </button>
        </div>
      </div>

      {/* Class List Panel */}
      <div className="class-list-panel">
        <div className="class-list-header">
          <h3 className="class-list-title">Current Class List:</h3>
          <span className="class-list-count">{classes.length}</span>
        </div>
        
        <div className="class-list">
          {classes.map((classItem, index) => (
            <div
              key={classItem.id}
              onClick={() => handleClassSelect(classItem)}
              className={`class-item ${selectedClass?.id === classItem.id ? 'selected' : ''}`}
            >
              <div className="class-item-code">
                {classItem.class_code}-{classItem.class_key}-{classItem.class_name}-{classItem.class_level}-{classItem.class_section}-{classItem.home_room}
              </div>
              <div className="class-item-details">
                Class Code: {classItem.class_code} | Key: {classItem.class_key} | Name: {classItem.class_name} | Level: {classItem.class_level}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mode Indicator */}
      <div className="mode-indicator">
        Mode: {mode}
      </div>

      {/* Instructions */}
      <div className="instructions-section">
        <h4 className="instructions-title">班別設定指引</h4>
        <div className="instructions-content">
          <p><strong>1. 班別資料的編修方法與教師資料的設定相似：</strong> 在策劃模組中，選資料(Data)，再選班別資料編修(Classes)。</p>
          <p><strong>2. 核對班別的資料：</strong> 如發覺畫面中缺少了一些需用的班別，用者須回到學年過渡策劃修正班別資料後，重新進行納取資料。</p>
          <p><strong>3. 系統如發現索引名稱(Key)相同：</strong> 會將後來的索引名稱改為以「~」符號為首的字串，用者可按「編修」(Modify)鍵進行修改。</p>
          <p><strong>4. 輸入完成後，按「儲存」(Save)鍵紀錄資料。</strong></p>
        </div>
      </div>
    </div>
  );
};

export default ClassSettings;
